import React, { useState } from "react";
import { useQuery } from "@apollo/client/react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import ActionModal from "./modals/action-modal";
import { CLIENTS_TABLE_QUERY } from "../constants/graphql/queries";
import useMutation from "../hooks/useMutation";
import useNotification from "../hooks/notification";
import { TRANSFER_PROJECT_MUTATION } from "../constants/graphql";

const TransferProjectModal = ({ project, clientLabel = "Client", projectLabel = "Project", open, onClose, onSuccess }) => {
  const [selectedClient, setSelectedClient] = useState(null);

  const { notifySuccess, notifyError } = useNotification();

  const { data: { clients = [] } = {}, loading } = useQuery(CLIENTS_TABLE_QUERY, {
    skip: !open,
  });

  const [transferProject, { loading: transferring }] = useMutation(TRANSFER_PROJECT_MUTATION);

  const handleClose = () => {
    setSelectedClient(null);
    if (onClose) onClose();
  };

  const handleTransfer = async () => {
    if (!selectedClient) {
      notifyError("Please select a client");
      return;
    }

    try {
      const { data } = await transferProject({
        variables: {
          projectId: project.id,
          targetClientId: selectedClient.id,
        },
      });
      
      const { project: transferredProject, errors } = data?.transferProject || {};
      
      if (errors && errors.length > 0) {
        const errorMessages = errors.map(e => e.message).join(", ");
        notifyError(errorMessages);
      } else if (transferredProject) {
        notifySuccess(`"${project.name}" has been successfully transferred!`);
        handleClose();
        if (onSuccess) onSuccess();
      } else {
        notifyError("Transfer failed - please try again");
      }
    } catch (error) {
      notifyError(error.message || "An error occurred during transfer");
    }
  };

  // Filter out the current client and only show active clients
  const availableClients = clients.filter(
    client => client.id !== project.client?.id && !client.disabled
  );

  return (
    <ActionModal
      title={`Transfer to Another ${clientLabel}`}
      titleFontSize="1.5rem"
      open={open}
      onClose={handleClose}
      maxWidth={600}
      showClose
    >
      <Box sx={{ py: 2 }}>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ mb: 3, textAlign: "center" }}
        >
          Transfer {(projectLabel || "Project").toLowerCase()} <strong>{project.name}</strong>
          <br />
          from <strong>{project.client?.name}</strong> to different {(clientLabel || "Client").toLowerCase()}.
        </Typography>

        <Autocomplete
          value={selectedClient}
          onChange={(event, newValue) => {
            setSelectedClient(newValue);
          }}
          options={availableClients}
          getOptionLabel={option => option.name}
          loading={loading}
          disabled={transferring}
          renderInput={params => (
            <TextField
              {...params}
              label={clientLabel}
              placeholder={`Select a ${(clientLabel || "Client").toLowerCase()}`}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={`No other active ${(clientLabel || "Client").toLowerCase()}s found`}
          sx={{ mb: 3 }}
        />

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={handleTransfer}
            disabled={!selectedClient || transferring}
          >
            {transferring ? <CircularProgress size={24} /> : `Transfer ${projectLabel}`}
          </Button>
        </Box>
      </Box>
    </ActionModal>
  );
};

export default TransferProjectModal;
